import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer, provideNgReflectAttributes, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { appRoutes } from './app.routes';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimations, provideNoopAnimations } from '@angular/platform-browser/animations';
import { RsCustomMatIconsModule } from '@rs-shared-lib/modules/rs-core/modules/rs-custom-mat-icons.module';
import { RsCoreModule } from '@rs-shared-lib/modules/rs-core/rs-core.module';
import { RsCoreTestingModule } from '@rs-shared-lib/modules/rs-core/rs-core-testing.module';
import { UserInfoCookieAppName } from '@rs-shared-lib/modules/rs-core/modules/rs-authentication/enums/UserInfoCookieAppName.enum';
import { environment } from '@environments/environment';
import { BASE_PATH as MOBIPAY_BASE_PATH } from '@api-client/mobipay';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import { RsRequestParamsFormatterInterceptor } from '@app/shared/interceptors/request-params/request-params.interceptor';
import { RS_LOADER_DEFAULT_SHOW_OPTIONS_TOKEN, ShowOptions } from '@rs-shared-lib/components/rs-loader/services/rs-loader.service';
import { DateAdapter } from '@angular/material/core';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, MatButtonToggleDefaultOptions } from '@angular/material/button-toggle';
import { RsAppInitializerReplaySubject } from '@rs-shared-lib/modules/rs-core/components/web-component-app-initializer/classes/RsAppInitializerReplaySubject';
import { finalize, firstValueFrom } from 'rxjs';
import { UserServiceFacade } from '@app/shared/services/user-details/user-details.service';
import { RsNativeDateAdapter } from '@rs-shared-lib/shared/adapters/rs-native-date-adapter';

const globalTooltipDefaultsOptions: MatTooltipDefaultOptions = {
  showDelay: 500,
  hideDelay: 0,
  touchendHideDelay: 0
};

const globalDialogDefaultsOptions: MatDialogConfig = {
  hasBackdrop: true,
  disableClose: false,
  autoFocus: true,
  restoreFocus: true,
  closeOnNavigation: true,
  width: '800px',
};

const globalMatFormFieldDefaultOptions: MatFormFieldDefaultOptions = {
  appearance: 'outline',
  subscriptSizing: 'dynamic',
};

const globalMatButtonToggleGroupConfig: MatButtonToggleDefaultOptions = {
  hideSingleSelectionIndicator: true,
  hideMultipleSelectionIndicator: true,
};

const globalRsLoaderDefaultShowOptionsOptions: Partial<ShowOptions> = { delay: 600 };

// Create app initializer condition for user details
export const userDetailsLoaded$ = new RsAppInitializerReplaySubject(1);

function getBaseProviders() {
  return [
    // Do not rely on ng-reflect attributes; they are planned for deprecation in the future.
    provideNgReflectAttributes(),
    provideZonelessChangeDetection(),
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(appRoutes, withComponentInputBinding()),
    provideAnimations(),
    provideEnvironmentNgxMask(),
    importProvidersFrom(RsCustomMatIconsModule),
    { provide: MOBIPAY_BASE_PATH, useValue: environment.apiUrl },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: globalMatFormFieldDefaultOptions },
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: globalDialogDefaultsOptions },
    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: globalTooltipDefaultsOptions },
    { provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, useValue: globalMatButtonToggleGroupConfig },
    { provide: HTTP_INTERCEPTORS, useClass: RsRequestParamsFormatterInterceptor, multi: true },
    { provide: DateAdapter, useClass: RsNativeDateAdapter },
  ];
}

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      RsCoreModule.forRoot({
        appName: UserInfoCookieAppName.REI,
        environment,
        additionalAppInitializerConditions: [userDetailsLoaded$],
      }),
    ),
    ...getBaseProviders(),
    // Initialize user details before the application starts
    provideAppInitializer(() => {
      const userService = inject(UserServiceFacade);

      return firstValueFrom(
        userService.getUserDetails().pipe(
          finalize(() => {
            userDetailsLoaded$.next(true);
            userDetailsLoaded$.complete();
          }),
        ),
      );
    }),
    {
      provide: RS_LOADER_DEFAULT_SHOW_OPTIONS_TOKEN,
      useValue: globalRsLoaderDefaultShowOptionsOptions,
    },
  ],
};

export const testAppConfig: ApplicationConfig = {
  providers: [
    // Add the test-specific provider
    importProvidersFrom(
      RsCoreTestingModule.forRoot({
        appName: UserInfoCookieAppName.REI,
        environment,
      }),
    ),
    ...getBaseProviders(),
    // Initialize user details before the application starts
    provideAppInitializer(() => {
      const userService = inject(UserServiceFacade);

      return firstValueFrom(
        userService.getUserDetails().pipe(
          finalize(() => {
            userDetailsLoaded$.next(true);
            userDetailsLoaded$.complete();
          }),
        ),
      );
    }),
    {
      provide: RS_LOADER_DEFAULT_SHOW_OPTIONS_TOKEN,
      useValue: { delay: 0 },
    },
    /**
     * provideNoopAnimations is mandatory for Playwright E2E tests to ensure environment stability.
     * * WHY WE USE THIS:
     * 1. WebKit Stability: Headless WebKit (Safari) frequently encounters 'Segmentation Faults'
     * when rendering heavy Angular Material animations (like mat-select) in a CI environment.
     * 2. Zoneless Conflict: Since this app uses `provideZonelessChangeDetection`, standard
     * animations can trigger microtask timing issues that lead to 'Target page closed' errors.
     * 3. Performance: It bypasses the browser's GPU-accelerated animation layer, significantly
     * reducing memory overhead and preventing test flakiness.
     * * NOTE ON DEPRECATION:
     * As of Angular 19, this is marked for removal in v23. When upgrading to v23,
     * this should be replaced with `provideAnimationsAsync('noop')`.
     */
    provideNoopAnimations()
  ],
};
